/**
 * User-specific storage utilities (web + native)
 * Each user gets their own isolated storage namespace.
 */
import { Platform } from "react-native";
import { storage } from "../services/storage";

// Native cache for current user ID (set by AuthContext; AsyncStorage is async so we can't read sync on native)
let _cachedUserId: string | null = null;

/** Set the current user ID cache (call from AuthContext when user is set/cleared). Required for native. */
export const setCurrentUserIdCache = (userId: string | null): void => {
  _cachedUserId = userId;
};

// Get the current user ID (from auth context or fallback)
export const getCurrentUserId = (): string => {
  // 🚨 MISSION 20: On native, use cache only (no sync localStorage/sessionStorage/window)
  if (Platform.OS !== "web") {
    const id = _cachedUserId ?? "default_user";
    if (!_cachedUserId) {
      console.log("🔍 USER ID: Native – no cache, using default_user");
    }
    return id;
  }

  // Web-only: sync resolution from localStorage / sessionStorage / URL
  console.log("🔍 USER ID: Resolving current user ID...");

  // PRIORITY 1: Check AuthContext user (for OAuth and email users)
  if (typeof window !== "undefined") {
    const cedarUser = typeof localStorage !== "undefined" ? localStorage.getItem("cedar_user") : null;
    console.log(`   Priority 1 (cedar_user):`, cedarUser ? "exists" : "null");

    if (cedarUser) {
      try {
        const parsedUser = JSON.parse(cedarUser);
        if (parsedUser.id) {
          console.log(`   ✅ RESOLVED: ${parsedUser.id} (from cedar_user)`);
          return parsedUser.id;
        }
      } catch (error) {
        console.error("Failed to parse cedar_user:", error);
      }
    }
  }

  // PRIORITY 2: Check session-like key (legacy test system) – web only
  const getSessionLikeUser = (): string | null => {
    if (typeof window === "undefined") return null;
    try {
      const v = window.sessionStorage?.getItem("currentTestUser");
      return v || null;
    } catch {
      return null;
    }
  };
  const sessionUser = getSessionLikeUser();
  console.log(`   Priority 2 (sessionStorage):`, sessionUser ? sessionUser : "null");
  if (sessionUser) {
    console.log(`   ✅ RESOLVED: ${sessionUser} (from sessionStorage)`);
    return sessionUser;
  }

  // PRIORITY 3: Try to get from current URL params (invite tokens) – web only
  if (typeof window !== "undefined") {
    const urlParams = new URLSearchParams(window.location.search);
    const token = urlParams.get("token");
    console.log(`   Priority 3 (URL token):`, token || "null");
    if (token && token.startsWith("test-user-")) {
      console.log(`   ✅ RESOLVED: ${token} (from URL)`);
      return token;
    }
  }

  // PRIORITY 4: Fallback to default key
  console.log(`   ⚠️ FALLBACK: Using "default_user" (no auth found)`);
  return "default_user";
};

// User-specific storage key builder
export const getUserStorageKey = (key: string, userId?: string): string => {
  const actualUserId = userId || getCurrentUserId();
  return `${actualUserId}_${key}`;
};

export const setUserStorage = async (key: string, value: string, userId?: string): Promise<void> => {
  const storageKey = getUserStorageKey(key, userId);
  await storage.setItem(storageKey, value);
};

export const getUserStorage = async (key: string, userId?: string): Promise<string | null> => {
  const storageKey = getUserStorageKey(key, userId);
  return storage.getItem(storageKey);
};

export const removeUserStorage = async (key: string, userId?: string): Promise<void> => {
  const storageKey = getUserStorageKey(key, userId);
  await storage.removeItem(storageKey);
};

export const clearAllUserStorage = async (userId?: string): Promise<void> => {
  const actualUserId = userId || getCurrentUserId();
  const keys = [
    "cedar_onboarding_completed",
    "cedar_onboarding_seen",
    "onboarding_tour_seen",
    "chat_messages",
  ];

  for (const key of keys) {
    await removeUserStorage(key, actualUserId);
  }

  // Also clear the auth context user
  await storage.removeItem("cedar_user");

  console.log(`🧹 Cleared all storage for user: ${actualUserId}`);
};

// Legacy cleanup - remove old non-user-specific keys
export const cleanupLegacyStorage = async (): Promise<void> => {
  const legacyKeys = [
    "cedar_onboarding_completed",
    "cedar_onboarding_seen",
    "cedar_onboarding_tour_seen",
    "chat_messages",
  ];

  for (const key of legacyKeys) {
    await storage.removeItem(key);
  }

  console.log("🧹 Cleaned up legacy localStorage keys");
};